import { Flex, Modal, Typography, message } from 'antd';
import { CloseOutlined, CreditCardOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CustomButton } from '@/components/ui';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { openModal } from '@/slice/modal';
import { lateCancelBooking } from '@/api/booking';

import { notifyActionError } from '@/helpers/subscriptionError';
interface LateCancelConfirmProps {
  open: boolean;
  onCancel: () => void;
  bookingId: string;
  consultantId: string;
  fee: string;
  // Threaded through so backing out to the policy modal preserves whether a
  // reschedule is still available. Losing it re-offered a rejected action.
  rescheduleClosed?: boolean;
}

// Final confirmation step for a franchisor's <24h cancellation: charges the
// late-cancellation fee and cancels the meeting. "KEEP MEETING" backs out to
// the policy modal without touching the booking.
export const LateCancelConfirm = (props: LateCancelConfirmProps) => {
  const { bookingId, consultantId, rescheduleClosed } = props;
  // Guard against callers that forgot to pass `fee` — never render literal
  // "undefined" in the copy below.
  const fee = props.fee ?? '$0';
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const cancelMutation = useMutation({
    mutationKey: ['late-cancel', bookingId],
    mutationFn: () => lateCancelBooking(bookingId),
    onSuccess: () => {
      message.success('Meeting canceled. The fee will be charged to your account.');
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['booking'] });
      props.onCancel();
    },
    // TCX-900: a Stripe outage must not read as a rejected cancellation.
    onError: (error) =>
      notifyActionError(error, 'Failed to cancel meeting. Something went wrong.'),
  });

  return (
    <Modal
      open={props.open}
      onCancel={props.onCancel}
      width={640}
      centered
      footer={null}
      closeIcon={<CloseOutlined style={{ color: '#000' }} />}
      styles={{ content: { borderRadius: 20 }, body: { padding: '18px 18px' } }}
    >
      <Flex vertical align={"center"} gap={16}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#FCE7E7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CreditCardOutlined style={{ fontSize: 24, color: '#DC2626' }} />
        </div>
        <Typography.Text style={{ fontSize: 22, fontWeight: 700, textAlign: 'center' }}>
          Cancel and pay the fee?
        </Typography.Text>
        <Typography.Text style={{ textAlign: 'center' }}>
          You&apos;re cancelling less than 24 hours before the meeting. The <b>{fee} meeting fee</b> will
          be charged to your account. This can&apos;t be undone.
        </Typography.Text>
      </Flex>
      <Flex vertical gap={12} style={{ marginTop: 24 }}>
        <CustomButton
          type={"primary"}
          text={"CONFIRM CANCELLATION"}
          loading={cancelMutation.isPending}
          onClick={() => cancelMutation.mutate()}
          style={{ backgroundColor: '#DC2626', borderColor: '#DC2626' }}
        />
        <CustomButton
          type={"default"}
          text={"KEEP MEETING"}
          onClick={() =>
            dispatch(
              openModal({
                modalType: 'LATE_CANCEL_POLICY',
                // Preserve the reschedule decision across the round trip —
                // without it the reopened policy modal re-offers an action the
                // backend refuses.
                modalProps: { bookingId, consultantId, fee, rescheduleClosed },
              }),
            )
          }
        />
      </Flex>
    </Modal>
  );
};
